import { Box, Link as MuiLink, Typography, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import NgwaahiaIcon from "./NgwaahiaIcon";
import useIsScrolling from "../hooks/useIsScrolling";
import {
  frontendRoutes,
  LANDING_STAY_QUERY_PARAM,
} from "../utility/frontendRoutes";

// Logged-in users are otherwise redirected away from "/" (see LandingPage),
// so this link needs the stay param or it would just bounce right back.
const LANDING_LINK = `${frontendRoutes.LANDING}?${LANDING_STAY_QUERY_PARAM}=true`;

// Slimmed-down counterpart to Landing/components/LandingFooter — same Legal
// and Need Help content, but drops Quick Links (its "#features"/"#faq" hrefs
// only resolve on the landing page itself) and the social icons, and uses
// the app's regular color tokens rather than landingColors (deliberately
// scoped to the landing page's own look, not the in-app pages). Collapsed
// into one compact row rather than LandingFooter's 3-column block, and
// pinned to the viewport bottom (position:fixed) rather than sitting in
// normal document flow — the page it's used on (HomePage) adds matching
// bottom padding so the fixed footer doesn't cover the last row of content.
const linkSx = {
  color: "#6b7280",
  textDecoration: "none",
  fontSize: 13,
  whiteSpace: "nowrap",
  "&:hover": { color: "#046287" },
};

const AppFooter = () => {
  // Mobile viewports are short enough that a permanently pinned footer eats a
  // meaningful slice of the feed, so it slides out of the way while scrolling
  // and slides back once the page settles. Desktop has the room; it stays put.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isScrolling = useIsScrolling();
  const isHidden = isMobile && isScrolling;

  return (
    <Box
      component="footer"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: "white",
        borderTop: "1px solid #e5e7eb",
        // translateY(100%) clears the border-top along with the content, so
        // nothing peeks above the viewport edge while hidden.
        transform: isHidden ? "translateY(100%)" : "none",
        transition: "transform 400ms ease-in-out",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", sm: "flex-start" },
            width: { xs: "100%", sm: "auto" },
            gap: 0.75,
          }}
        >
          <Box
            component={Link}
            to={LANDING_LINK}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              textDecoration: "none",
              // // Extra inset on mobile only, where the icon sits alone against
              // // the screen edge with no wordmark to soften it.
              ml: { xs: 1.5, sm: 0 },
            }}
          >
            <NgwaahiaIcon size={18} color="#046287" />
            {/* The copyright line sitting right beside it already spells out
                the name on mobile, so the wordmark is redundant there. */}
            <Typography
              fontWeight={700}
              fontSize={13}
              color="#046287"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              Ngwaahia
            </Typography>
          </Box>

          {/* Shown here on mobile so the logo row and copyright share one
            line; the standalone copyright below takes over from sm up. */}
          <Typography
            variant="caption"
            sx={{
              display: { xs: "block", sm: "none" },
              color: "#9ca3af",
              whiteSpace: "nowrap",
            }}
          >
            &copy; {new Date().getFullYear()} Ngwaahia
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            width: { xs: "100%", sm: "auto" },
            gap: { xs: 1.25, sm: 2.5 },
          }}
        >
          <MuiLink
            component={Link}
            to={frontendRoutes.PRIVACY_POLICY}
            sx={linkSx}
          >
            Privacy Policy
          </MuiLink>
          <MuiLink
            component={Link}
            to={frontendRoutes.TERMS_OF_SERVICE}
            sx={linkSx}
          >
            Terms of Service
          </MuiLink>
          <MuiLink component={Link} to={frontendRoutes.HELP} sx={linkSx}>
            Help
          </MuiLink>
          <MuiLink component={Link} to={frontendRoutes.SUPPORT} sx={linkSx}>
            Support
          </MuiLink>
          {/* Dropped on mobile to keep the footer to two lines there; it
            duplicates the Support page's own contact info. */}
          <MuiLink
            href="mailto:support@ngwaahia.com"
            sx={{ ...linkSx, display: { xs: "none", sm: "inline" } }}
          >
            support@ngwaahia.com
          </MuiLink>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: { xs: "none", sm: "block" },
            color: "#9ca3af",
            whiteSpace: "nowrap",
          }}
        >
          &copy; {new Date().getFullYear()} Ngwaahia
        </Typography>
      </Box>
    </Box>
  );
};

export default AppFooter;
